import { FC, useState } from 'react';
import Image from 'next/image';
import cn from 'classnames';
import styled from 'styled-components';

import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { Button } from '@/shared/uikit/Button';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { ModalSlideContainer } from '@/widgets/features/ModalSlideContainer';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { ScenarioTestLaunchModalProps } from '@/widgets/modals/types';

import { getImageUrl } from './helpers';

const steps = {
  step1: {
    image: 'ready-to-launch.png',
    list: Array(3).fill(<Icon name="warning-circle" size="s16" />),
  },
  step2: {
    image: 'success-launch.png',
    list: Array(2).fill(
      <Icon name="check-circle" size="s16" style={{ color: COLORS.systemForest200 }} />,
    ),
  },
} as const;

export const ScenarioTestLaunchModal: FC<ScenarioTestLaunchModalProps> = ({ testScenario }) => {
  const modals = useModalsStore();
  const { t } = useTranslation('scenarios', {
    keyPrefix: 'scenarioTest',
  });

  const [step, setStep] = useState<'step1' | 'step2'>('step1');
  const isDesktop = useIsDesktop();
  const handleClose = () => modals.closeModal();

  const handleLaunch = async () => {
    const resp = await testScenario();
    if (resp) setStep('step2');
  };

  const isStep1 = step === 'step1';
  const device = isDesktop ? 'desktop' : 'mobile';

  return (
    <ModalSlideContainer
      title={t('title')}
      footer={null}
      open
      onCancel={handleClose}
      width={600}
      drawerHeight="100vh"
      drawerStyles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Container>
        <ul className="info-list">
          {steps[step].list.map((icon, idx) => (
            <li className="info-list-li" key={idx}>
              <div className="info-icon-container">{icon}</div>
              <div>{t(`${step}Item${idx}`)}</div>
            </li>
          ))}
        </ul>
        <Image
          layout="responsive"
          alt="alt"
          src={getImageUrl(device, steps[step].image)}
          width={400}
          height={400}
        />
      </Container>
      <Footer className={cn(step)}>
        <div className="buttons">
          <Button type="default" className="button-close" onClick={handleClose}>
            {isStep1 ? t('buttonBackClose') : t('buttonClose')}
          </Button>
          {isStep1 && (
            <Button type="primary" className="button-launch" onClick={handleLaunch}>
              {t('buttonLaunch')}
            </Button>
          )}
        </div>
      </Footer>
    </ModalSlideContainer>
  );
};

// ===== styles =====

const Container = styled.div`
  padding: 16px 16px 0 16px;

  ${media.desktop} {
    border-top: 1px solid ${COLORS.systemDark100};
    border-bottom: 1px solid ${COLORS.systemDark100};
    padding: 24px;
  }

  .info-list {
    font-size: 14px;
    background: ${COLORS.systemLight200};
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 16px;
  }

  .info-list-li {
    list-style: none;
    display: flex;
    gap: 8px;
  }

  .info-list-li ~ li {
    margin-top: 12px;
  }

  .info-icon-container {
    display: flex;
    align-items: start;
    padding-top: 1px;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-inline: 16px;
  padding-top: 16px;
  gap: 28px;

  ${media.desktop} {
    padding-bottom: 16px;
    padding-inline: 24px;
    flex-direction: row;
    justify-content: end;
  }

  .buttons {
    height: 44px;
    display: grid;
    gap: 16px;
    width: 100%;
    grid-template-columns: 1fr 1fr;

    ${media.desktop} {
      width: 163px;
    }
  }

  &.step2 .buttons {
    grid-template-columns: 1fr;

    ${media.desktop} {
      width: fit-content;
    }
  }

  .button-close {
    height: inherit;
  }

  .button-launch {
    height: inherit;
  }
`;
